// App-wide mutable state.
//
// This module owns the singleton `state` object plus localStorage persistence
// and the members cache. Everything else reads/mutates `state` directly today;
// when we move to Preact + Signals, this becomes the seam where reactivity is
// introduced.

export const DEFAULT_CONFIG = {
  apiBase: "https://44b9llt4o1.execute-api.ap-northeast-2.amazonaws.com/dev",
  clubSlug: null as string | null,
};

export const STORAGE_KEY_BASE = "tennis-club-app.v3";
export const LAST_CLUB_KEY = "tennis-club-app.last-club";

// Canonical origin for share links (invite/guest/roster). Fixed so links don't
// leak the CloudFront domain when admins happen to open the app there.
export const SHARE_ORIGIN = "https://fairmatch.kr";

export const BRAND = {
  name: "페어매치",
  nameEn: "FairMatch",
  tagline: "테니스 클럽 운영 앱",
  contactEmail: "fairmatch.team@gmail.com",
};

export const TAB_ROUTES = new Set(["home", "events", "stats"]);

export const MEMBERS_CACHE_TTL_MS = 5 * 60 * 1000;

export interface AppState {
  config: { apiBase: string; clubSlug: string | null };
  session: any;
  club: any;
  tab: "home" | "events" | "stats";
  route: any;
  pendingRoute: any;
  eventFilter: string;
  memberStatusFilter: string;
  membersCache: { promise: Promise<any>; fetchedAt: number } | null;
}

export const state: AppState = {
  config: { ...DEFAULT_CONFIG },
  session: null,
  club: null,
  tab: "home",
  route: { name: "home" },
  pendingRoute: null,
  eventFilter: "all",
  memberStatusFilter: "active",
  membersCache: null,
};

export function storageKeyForClub(slug: string | null | undefined): string {
  return slug ? `${STORAGE_KEY_BASE}.${slug}` : STORAGE_KEY_BASE;
}

export function loadPersisted(): void {
  if (!state.config.clubSlug) return;
  try {
    const raw = localStorage.getItem(storageKeyForClub(state.config.clubSlug));
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed.session && typeof parsed.session === "object") {
      state.session = parsed.session;
    }
    if (parsed.club && typeof parsed.club === "object") {
      state.club = parsed.club;
    }
  } catch (e) {
    console.warn("persisted load failed", e);
  }
}

export function persist(): void {
  if (!state.config.clubSlug) return;
  try {
    localStorage.setItem(
      storageKeyForClub(state.config.clubSlug),
      JSON.stringify({
        session: state.session,
        club: state.club,
      }),
    );
    localStorage.setItem(LAST_CLUB_KEY, state.config.clubSlug);
  } catch (e) {
    console.warn("persist failed", e);
  }
}

// fetchMembers depends on the API layer; the legacy module wires it up by
// passing in the api() function via setMembersFetcher to avoid an import cycle.
type ApiFn = (path: string) => Promise<any>;
let _apiFn: ApiFn | null = null;

export function setMembersFetcherApi(api: ApiFn): void {
  _apiFn = api;
}

export function fetchMembers({ force = false }: { force?: boolean } = {}): Promise<any> {
  if (!_apiFn) {
    throw new Error("fetchMembers called before api() was registered");
  }
  const cached = state.membersCache;
  if (!force && cached && Date.now() - cached.fetchedAt < MEMBERS_CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = _apiFn("/api/v1/members").catch((err) => {
    if (state.membersCache && state.membersCache.promise === promise) {
      state.membersCache = null;
    }
    throw err;
  });
  state.membersCache = { promise, fetchedAt: Date.now() };
  return promise;
}

export function prefetchMembers(): void {
  fetchMembers().catch(() => {});
}

export function invalidateMembersCache(): void {
  state.membersCache = null;
}
